/**
 * LocaleSwitcher - 语言切换器（图标按钮 + 下拉列表）。
 *
 * 触发器：国旗 + 完整语言名（🇨🇳 简体中文 / 🇺🇸 English）。
 * 下拉：列出所有支持语言（国旗 + 完整名），当前项打勾；点选即切换：
 * /:lang 页导航到对应前缀 URL（LangSync 同步 i18next），英文独占路由
 * （/blog 等）就地切 i18n 语言、停留在当前页（见 switchTo 注释）。
 * 交互：点外部 / Esc 关闭；aria-expanded/haspopup 满足键盘与读屏可达性。
 * URL 是语言真相，切换即导航（LangSync 自动同步 i18next + Clerk）。
 */

import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { SUPPORTED_LANGS, type Lang } from "../i18n";
import { useLang, stripLang, rememberLang } from "../lib/lang";
import { Check } from "./icons";

/** 国旗 emoji（完整语言名走 i18n locale.* ） */
const LANG_FLAG: Record<Lang, string> = {
  zh: "🇨🇳",
  ja: "🇯🇵",
  ko: "🇰🇷",
  en: "🇺🇸",
};

export default function LocaleSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = useLang();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // 触发器与下拉关联，供读屏软件识别
  const listId = useId();

  // 切换到目标语言：
  // - /:lang 页面：拼新前缀的当前页 URL（保留 search/hash），导航后由
  //   LangSync 同步 i18next；同页切语言的滚动保留见 ScrollToTop/LangSync。
  // - 英文独占路由（/blog 等无 /:lang 前缀，stripLang 原样返回）：没有其他
  //   语言版本的页面可去（原样拼前缀会得到 /zh/blog 落 404），就地切换
  //   UI 语言——顶栏菜单即时跟随所选语言，停留在当前页。偏好已写入
  //   localStorage（rememberLang），后续进入任何 /:lang 页由 LangSync 接管，
  //   顶栏链接（PublicLayout useLang → detectLang）也会拼出新语言前缀。
  function switchTo(lang: Lang) {
    setOpen(false);
    if (lang === currentLang) return;
    rememberLang(lang);
    const bare = stripLang(location.pathname);
    if (bare === location.pathname) {
      void i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      return;
    }
    navigate(`/${lang}${bare || ""}${location.search}${location.hash}`);
  }

  // 点外部关闭
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Esc 关闭
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // full 名走 i18n（locale.zh / locale.en）；short 走 LANG_SHORT
  return (
    <div className="locale-switcher" ref={wrapperRef}>
      {/* aria-label 必须包含可见文本（axe label-content-name-mismatch：
          读屏用户听到的名称应与屏幕上看到的"🇺🇸 English"一致可对应），
          故拼成「切换语言：English」而非仅"切换语言" */}
      <button
        type="button"
        className="locale-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${t("locale.switch")}: ${t(`locale.${currentLang}`)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="locale-trigger-flag" aria-hidden>
          {LANG_FLAG[currentLang]}
        </span>
        <span className="locale-trigger-code">{t(`locale.${currentLang}`)}</span>
      </button>

      {open && (
        <ul id={listId} role="menu" className="locale-menu">
          {SUPPORTED_LANGS.map((lang) => {
            const isActive = lang === currentLang;
            return (
              <li key={lang} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  className={`locale-menu-item ${isActive ? "active" : ""}`}
                  onClick={() => switchTo(lang)}
                >
                  <span className="locale-menu-label">
                    <span className="locale-menu-flag" aria-hidden>
                      {LANG_FLAG[lang]}
                    </span>
                    {t(`locale.${lang}`)}
                  </span>
                  {isActive && (
                    <span className="locale-menu-check" aria-hidden>
                      <Check size={14} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
